/**
 * A project as a card, the way the workbench's Projects grid and Home draw one (its ProjectDashboardCard): the
 * name and where it sits, a line of purpose from its roadmap, what waits on the person, what is working and the
 * plans waiting, a quiet strip of the last fortnight's runs, its health in words, the team's faces and the machines
 * on it. The Projects list and the Portfolio draw the same card from `GET /projects/v1/overview`, so a project reads
 * the same in both. Also the two short dialogs both pages open: a new project, and moving one to another area.
 */

import { CLASSIFICATION_DESCRIPTIONS, CLASSIFICATION_ORDER, formatMoney, type Classification, type ProjectOverview, type ProjectOverviewCard } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { Copy, Ellipsis, FolderArrowRight, Persons } from '@gravity-ui/icons';
import { Button, Dropdown, Label, Tooltip } from '@heroui/react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/context.ts';
import { groupsAvailable } from '../directory.ts';
import { isCloud } from '../edition.ts';
import { FormModal } from './cloud/FormModal.tsx';
import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { LEVEL_TEXT } from './memoryAccessModel.ts';
import { MemberFace } from './teams/MemberFace.tsx';
import { ChoiceSelect, inputClass, StatusMark } from './ui.tsx';
import type { ArtTone } from './ui/Art.tsx';
import { toneFor } from './ui/ChoiceCard.tsx';
import { FitText } from './ui/FitText.tsx';

/** A project takes its area's tone, so an area reads as one family; one under no area its own. */
export const projectTone = (c: Pick<ProjectOverviewCard, 'id' | 'area'>): ArtTone => toneFor(c.area ? `area:${c.area.id}` : c.id);
export const areaTone = (areaId: string): ArtTone => toneFor(`area:${areaId}`);

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** "On jp-laptop", "On jp-laptop and build-node", "On 4 machines · 2 online", "No machine has it yet". */
export function machinesWords(m: ProjectOverviewCard['machines']): string {
  if (!m.total) return 'No machine has it yet';
  const online = m.online === m.total ? (m.total === 1 ? ' · online' : ' · all online') : m.online ? ` · ${m.online} online` : ' · none online';
  if (m.total <= 2 && m.names.length === m.total) return `On ${m.names.join(' and ')}${online}`;
  return `On ${plural(m.total, 'machine')}${online}`;
}

/** The fortnight's runs as quiet bars in the card's tone; the words say the same for a screen reader and a hover. */
function ActivityStrip({ values, days }: { values: number[]; days: string[] }) {
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const busiest = total ? days[values.indexOf(Math.max(...values))] : undefined;
  const words = total ? `${plural(total, 'run')} in the last two weeks${busiest ? `, the most on ${dateMedium(`${busiest}T00:00:00Z`, { year: false, timeZone: 'UTC' })}` : ''}` : 'No runs in the last two weeks';
  const w = 6;
  const gap = 3;
  const h = 22;
  return (
    <svg viewBox={`0 0 ${values.length * (w + gap) - gap} ${h}`} className="h-[22px] w-full max-w-[9.5rem] shrink-0" role="img" aria-label={words} preserveAspectRatio="none">
      <title>{words}</title>
      {values.map((v, i) => {
        const bar = v ? Math.max(3, Math.round((v / max) * h)) : 2;
        return <rect key={i} x={i * (w + gap)} y={h - bar} width={w} height={bar} rx={1.5} fill="var(--art)" opacity={v ? 0.75 : 0.18} />;
      })}
    </svg>
  );
}

/** The team's faces, a little overlapped, and how many more. */
function Faces({ card }: { card: ProjectOverviewCard }) {
  if (!card.faces.length) return null;
  const names = card.faces.map((f) => f.name);
  const shown = card.faces.slice(0, 4);
  const more = card.faces.length - shown.length + card.moreFaces;
  const label = `${card.teams ? 'Its team' : 'Its people'}: ${names.join(', ')}${card.moreFaces ? ` and ${card.moreFaces} more` : ''}`;
  const faces = (
    <>
      <span className="flex -space-x-2 [&>*]:rounded-full [&>*]:ring-2 [&>*]:ring-[var(--surface)]" aria-hidden="true">
        {shown.map((f, i) => (
          <MemberFace key={`${f.name}-${i}`} member={{ ...f, human: f.human ?? false }} index={i} />
        ))}
      </span>
      {more ? <span className="ps-1.5 text-xs text-muted tabular-nums" aria-hidden="true">+{more}</span> : null}
    </>
  );
  // A team's faces open that team's own page; the people of a project with no team are only shown.
  const team = card.teamLinks[0];
  return team ? (
    <Link to={`/teams/${encodeURIComponent(team.id)}`} className="relative z-10 flex shrink-0 items-center rounded-full" aria-label={`${label}. Open ${team.name}`} title={`${label}. Open ${team.name}`}>
      {faces}
    </Link>
  ) : (
    <span className="flex shrink-0 items-center" role="img" aria-label={label} title={label}>
      {faces}
    </span>
  );
}

function Number({ value, label, needsYou }: { value: number; label: string; needsYou?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dd className={`m-0 text-card font-semibold tabular-nums ${needsYou && value ? 'text-warning' : 'text-foreground'}`}>{value.toLocaleString()}</dd>
      <dt className="text-xs text-muted">{label}</dt>
    </div>
  );
}

export interface CardActions {
  /** Offered when the person may file it elsewhere (a portfolio manager, or managing its area): `canMove` says for which. */
  onMove?: (card: ProjectOverviewCard) => void;
  canMove?: (card: ProjectOverviewCard) => boolean;
}

/** One project. The whole card opens it; the one menu holds the rest. */
export function ProjectCard({ card: c, overview, actions, showArea = true }: { card: ProjectOverviewCard; overview: Pick<ProjectOverview, 'days' | 'money'>; actions?: CardActions; showArea?: boolean }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const archived = c.state === 'archived';
  const where = isCloud() ? undefined : c.area ? c.area.name : 'Not in an area';
  const repositories = c.repositories ? plural(c.repositories, 'repository', 'repositories') : 'No repositories yet';
  const line = [showArea ? where : undefined, repositories].filter(Boolean).join(' · ');
  const tasks = c.tasks.open ? `${plural(c.tasks.open, 'task')} open` : 'No open tasks';
  const spend = c.spendUsd ? `${formatMoney(c.spendUsd, overview.money, { compact: true })} in 30 days` : undefined;
  return (
    <article
      className={`card-art flex h-full min-w-0 flex-col gap-2.5 rounded-2xl p-4 has-[a:focus-visible]:outline-2 has-[a:focus-visible]:outline-offset-2 has-[a:focus-visible]:outline-accent ${archived ? 'opacity-75' : ''}`}
      data-tone={projectTone(c)}
      data-motif="arc"
      data-pressable
      data-project-card={c.id}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* The name is the card's link, stretched over the whole card; the menu sits above it. */}
          <Link to={`/projects/${encodeURIComponent(c.id)}`} className="block min-w-0 text-card font-semibold leading-snug text-foreground outline-none after:absolute after:inset-0 after:content-['']">
            <FitText className="block">{c.name}</FitText>
          </Link>
          <FitText className="block text-xs text-muted">{line}</FitText>
        </div>
        <Dropdown>
          <Button size="sm" variant="ghost" isIconOnly aria-label={`More for ${c.name}`} className="relative z-10 -me-1.5 -mt-1 shrink-0">
            <Ellipsis className="size-4" />
          </Button>
          <Dropdown.Popover placement="bottom end" className="min-w-52">
            <Dropdown.Menu
              aria-label={`More for ${c.name}`}
              onAction={(key) => {
                if (key === 'move') actions?.onMove?.(c);
                if (String(key).startsWith('team:')) navigate(`/teams/${encodeURIComponent(String(key).slice(5))}`);
                if (key === 'copy')
                  void navigator.clipboard?.writeText(c.id).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
              }}
            >
              {actions?.onMove && (actions.canMove?.(c) ?? true) ? (
                <Dropdown.Item id="move" textValue="Move to another area">
                  <FolderArrowRight className="size-4 text-muted" />
                  <Label>Move to another area…</Label>
                </Dropdown.Item>
              ) : null}
              {c.teamLinks.map((t) => (
                <Dropdown.Item key={t.id} id={`team:${t.id}`} textValue={`Open the team ${t.name}`}>
                  <Persons className="size-4 text-muted" />
                  <Label>Open {t.name}</Label>
                </Dropdown.Item>
              ))}
              <Dropdown.Item id="copy" textValue="Copy the project id">
                <Copy className="size-4 text-muted" />
                <Label>{copied ? 'Copied' : 'Copy the project id'}</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>

      {c.purpose ? (
        <FitText lines={2} className="min-h-[2lh] text-sm text-foreground/85">
          {c.purpose}
        </FitText>
      ) : (
        <p className="m-0 min-h-[2lh] text-sm text-muted">No vision on its roadmap yet.</p>
      )}

      <dl className="m-0 grid grid-cols-3 gap-x-3">
        <Number value={c.needsYou} label="need you" needsYou />
        <Number value={c.running} label="working now" />
        <Number value={c.plansWaiting} label={c.plansWaiting === 1 ? 'plan waiting' : 'plans waiting'} />
      </dl>

      <div className="flex min-w-0 items-end justify-between gap-3">
        <ActivityStrip values={c.activity} days={overview.days} />
        <FitText className="min-w-0 text-end text-xs text-muted tabular-nums">{spend ? `${tasks} · ${spend}` : tasks}</FitText>
      </div>

      <div className="mt-auto flex min-w-0 items-center justify-between gap-3 border-t border-separator/60 pt-2.5">
        <div className="min-w-0">
          {c.health.why ? (
            <Tooltip delay={300}>
              <Tooltip.Trigger className="relative z-10 inline-flex cursor-default rounded-sm" tabIndex={0}>
                <StatusMark state={c.health.mark} label={c.health.words} />
              </Tooltip.Trigger>
              <Tooltip.Content>{c.health.why}</Tooltip.Content>
            </Tooltip>
          ) : (
            <StatusMark state={c.health.mark} label={c.health.words} />
          )}
          <FitText className="block text-xs text-muted">{archived ? 'Archived; an owner restores it from its page' : machinesWords(c.machines)}</FitText>
        </div>
        <Faces card={c} />
      </div>
    </article>
  );
}

/** Cards in a grid that re-rows by its own width: one column on a phone, two to three on a desk. */
export function ProjectGrid({ cards, overview, actions, showArea }: { cards: ProjectOverviewCard[]; overview: Pick<ProjectOverview, 'days' | 'money'>; actions?: CardActions; showArea?: boolean }) {
  return (
    <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))] gap-3 p-0">
      {cards.map((c) => (
        <li key={c.id} className="min-w-0">
          <ProjectCard card={c} overview={overview} actions={actions} showArea={showArea} />
        </li>
      ))}
    </ul>
  );
}

const NO_AREA = '__none';

/** The areas a person may file a new project under, and whether "no area" is one of the choices. */
export function creatableAreas(o: ProjectOverview): { areas: Array<{ id: string; name: string }>; none: boolean } {
  const any = o.can.admin || o.can.portfolioManager;
  return { areas: o.areas.filter((a) => any || o.can.manages.includes(a.id)).map((a) => ({ id: a.id, name: a.name })), none: any };
}

/**
 * A new project: a name, where it sits, who owns it (the person making it, to start). The platform gives it its id;
 * a workbench links to it when its person opens it from the organisation's list. The id from a workbench's own
 * project is asked for only behind "It already exists on a workbench", for filing it before that workbench signs in.
 */
export function NewProjectDialog({ isOpen, onClose, overview, area }: { isOpen: boolean; onClose: () => void; overview: ProjectOverview; area?: string }) {
  const navigate = useNavigate();
  const { account } = useAuth();
  const choice = creatableAreas(overview);
  const firstArea = area ?? (choice.none ? NO_AREA : (choice.areas[0]?.id ?? NO_AREA));
  const [name, setName] = useState('');
  const [where, setWhere] = useState(firstArea);
  const [owners, setOwners] = useState<Array<{ id: string; name: string; type?: 'user' | 'group' }>>(account?.id ? [{ id: account.id, name: account.name ?? account.email ?? 'You' }] : []);
  const [classification, setClassification] = useState<Classification>();
  const [existing, setExisting] = useState(false);
  const [id, setId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const businessAreaId = isCloud() || where === NO_AREA ? undefined : where;
  const idOk = !existing || /^[A-Za-z0-9_.:-]{1,200}$/.test(id.trim());
  const create = async () => {
    setBusy(true);
    setError(undefined);
    if (!classification) return;
    try {
      const body = { name: name.trim(), classification, owners: owners.filter((o) => o.type !== 'group').map((o) => o.id), ownerGroups: owners.filter((o) => o.type === 'group').map((o) => o.id), ...(businessAreaId ? { businessAreaId } : {}), ...(existing && id.trim() ? { id: id.trim() } : {}) };
      const made = overview.can.admin ? await routes.createProject(body) : await routes.createAreaProject(body);
      onClose();
      navigate(`/projects/${encodeURIComponent(made.id)}`);
    } catch (e) {
      const message = (e as Error).message;
      setError(message === 'exists' ? 'A project already has that id. Open it from the list instead.' : message);
    } finally {
      setBusy(false);
    }
  };
  const areaOptions: Record<string, string> = { ...(choice.none ? { [NO_AREA]: 'Not in an area yet' } : {}), ...Object.fromEntries(choice.areas.map((a) => [a.id, a.name])) };
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="New project"
      blurb="People open it from their workbench's list of the organisation's projects, and it links to their repository there."
      submitLabel="Create project"
      canSubmit={Boolean(name.trim()) && Boolean(classification) && idOk && (choice.none || Boolean(businessAreaId))}
      busy={busy}
      error={error}
      onSubmit={() => void create()}
    >
      <label className="grid gap-1 text-xs text-muted">
        Name
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer portal" autoFocus maxLength={200} />
      </label>
      <div className="grid gap-1 text-xs text-muted">
        Classification
        <ChoiceSelect label="Classification" className="w-full" value={classification} placeholder="How sensitive is its data?" options={Object.fromEntries(CLASSIFICATION_ORDER.map((c) => [c, LEVEL_TEXT[c]])) as Record<Classification, string>} onChange={setClassification} />
        <span>{classification ? `${CLASSIFICATION_DESCRIPTIONS[classification]} Members read its memory up to ${LEVEL_TEXT[classification]}.` : "Members read the project's memory up to its classification, and it decides which AI providers its work may use."}</span>
      </div>
      {!isCloud() && Object.keys(areaOptions).length > 1 ? <ChoiceSelect label="Business area" className="w-full" value={where} options={areaOptions} onChange={setWhere} /> : null}
      <div className="grid gap-1 text-xs text-muted">
        Owners
        {owners.length > 0 && (
          <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-sm text-foreground">
            {owners.map((o) => (
              <li key={o.id} className="inline-flex items-center gap-1">
                <SubjectName id={o.id} fallback={o.name} />
                {o.type === 'group' ? <span className="text-xs text-muted">(group)</span> : null}
                <button type="button" className="cursor-[var(--cursor-interactive)] text-muted hover:text-foreground" aria-label={`Remove ${o.name}`} onClick={() => setOwners(owners.filter((x) => x.id !== o.id))}>
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <SubjectPicker kinds={['user', 'group']} placeholder={groupsAvailable() ? 'Add a person or a group by name' : 'Add an owner by name'} onPick={(s) => setOwners([...owners.filter((o) => o.id !== s.id), { id: s.id, name: s.name, type: s.type }])} />
      </div>
      <details className="group text-sm" open={existing} onToggle={(e) => setExisting((e.target as HTMLDetailsElement).open)}>
        <summary className="cursor-[var(--cursor-interactive)] text-xs text-muted hover:text-foreground">It already exists on a workbench</summary>
        <div className="mt-2 grid gap-1 text-xs text-muted">
          <span>
            A workbench files its own projects here the first time it signs in, so this is rarely needed. To file one before then, give the id it keeps in <span className="mono">.mojoup/project.json</span>.
          </span>
          <input className={`${inputClass} mono`} value={id} onChange={(e) => setId(e.target.value)} placeholder="prj_…" aria-label="The workbench's project id" />
        </div>
      </details>
    </FormModal>
  );
}

/** File a project under another area, or under none (a portfolio manager). */
export function MoveProjectDialog({ card, overview, onClose, onMoved }: { card: ProjectOverviewCard | undefined; overview: ProjectOverview; onClose: () => void; onMoved: () => void }) {
  const choice = creatableAreas(overview);
  const [to, setTo] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const current = card?.area?.id ?? NO_AREA;
  const value = to ?? current;
  const options: Record<string, string> = { ...(overview.can.portfolioManager ? { [NO_AREA]: 'Not in an area' } : {}), ...Object.fromEntries(choice.areas.map((a) => [a.id, a.name])) };
  const move = async () => {
    if (!card) return;
    setBusy(true);
    setError(undefined);
    try {
      await routes.moveProject(card.id, value === NO_AREA ? null : value);
      setTo(undefined);
      onMoved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormModal isOpen={!!card} onClose={() => (setTo(undefined), onClose())} title={`Move ${card?.name ?? 'the project'}`} blurb="Its new area's managers own it with its members; its spend is booked to that area from now on." submitLabel="Move" canSubmit={value !== current} busy={busy} error={error} onSubmit={() => void move()}>
      <ChoiceSelect label="Business area" className="w-full" value={value} options={options} onChange={setTo} />
    </FormModal>
  );
}
